import React from 'react';
import { SubmitButton, Header4, Button, ButtonColor, ButtonSize, useFormContext, SimpleBlock } from 'ui-builder';
import FormTemplate from '../../../shared/uibuilder/form/FormTemplate';
import Row6x6x from '../../../shared/web/uitheme/form/Row6x6x';
import { ColorPickerWithText } from './ColorPickerWithText';
import { ThemeDto } from '../Domain/clientPortalService';

export type BrandingFormData = {
  logoFileId?: number;
  _logoUrl?: {
    resized: string;
    full: string;
  };
  theme: ThemeDto;
};

interface ClientPortalBrandingFormProps {
  initialData: BrandingFormData;
  onSubmit: (data: BrandingFormData) => Promise<BrandingFormData>;
}

const defaultColors = {
  fontColor1: '#426377',
  fontColor2: '#FFFFFF',
  detailBoxHighlightColor1: '#4cd964',
  detailBoxHighlightColor2: '#4cd964',
  detailBoxShadingColor1: '#dde7eb4d',
  detailBoxShadingColor2: '#eefcf0',
};

function ResetColorsButton() {
  const { onChangeCallback } = useFormContext();

  const handleReset = () => {
    if (onChangeCallback) {
      onChangeCallback({
        'theme.fontColor1': defaultColors.fontColor1,
        'theme.fontColor2': defaultColors.fontColor2,
        'theme.detailBoxHighlightColor1': defaultColors.detailBoxHighlightColor1,
        'theme.detailBoxHighlightColor2': defaultColors.detailBoxHighlightColor2,
        'theme.detailBoxShadingColor1': defaultColors.detailBoxShadingColor1,
        'theme.detailBoxShadingColor2': defaultColors.detailBoxShadingColor2,
      });
    }
  };

  return (
    <Button
      text="Reset to Default Colors"
      color={ButtonColor.GRAY}
      size={ButtonSize.SMALL}
      isFilled={true}
      onClick={handleReset}
    />
  );
}

export default function ClientPortalBrandingForm(props: ClientPortalBrandingFormProps) {

  // Prepare initial data with theme colors as flat fields for form
  const formInitialData = {
    ...props.initialData,
    // Map theme fields to form field names with "theme." prefix
    ...(props.initialData.theme
      ? {
          'theme.fontColor1': props.initialData.theme.fontColor1,
          'theme.fontColor2': props.initialData.theme.fontColor2,
          'theme.detailBoxHighlightColor1': props.initialData.theme.detailBoxHighlightColor1,
          'theme.detailBoxHighlightColor2': props.initialData.theme.detailBoxHighlightColor2,
          'theme.detailBoxShadingColor1': props.initialData.theme.detailBoxShadingColor1,
          'theme.detailBoxShadingColor2': props.initialData.theme.detailBoxShadingColor2,
        }
      : {}),
  };

  const colorValidation = {
    type: 'string' as const,
    constraints: {
      required: true,
      regex: {
        value: /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/,
        message: 'Must be a valid hex color (#RRGGBB or #RRGGBBAA)',
      },
    },
  };

  const validationSchema = {
    'theme.fontColor1': colorValidation,
    'theme.fontColor2': colorValidation,
    'theme.detailBoxHighlightColor1': colorValidation,
    'theme.detailBoxHighlightColor2': colorValidation,
    'theme.detailBoxShadingColor1': colorValidation,
    'theme.detailBoxShadingColor2': colorValidation,
  };

  return (
    <FormTemplate<any>
      submitFormFunc={async (data) => {
        // Build theme object from flat fields
        const theme: ThemeDto = {
          fontColor1: data['theme.fontColor1'],
          fontColor2: data['theme.fontColor2'],
          detailBoxHighlightColor1: data['theme.detailBoxHighlightColor1'],
          detailBoxHighlightColor2: data['theme.detailBoxHighlightColor2'],
          detailBoxShadingColor1: data['theme.detailBoxShadingColor1'],
          detailBoxShadingColor2: data['theme.detailBoxShadingColor2'],
        };

        const brandingData: BrandingFormData = {
          logoFileId: data.logoFileId,
          _logoUrl: data._logoUrl,
          theme,
        };

        return await props.onSubmit(brandingData);
      }}
      initialData={formInitialData}
      validationSchema={validationSchema}
      buttons={
        <SubmitButton text="Save Changes"  />
      }
    >
      <Header4>Logo</Header4>

      <Header4>Theme Colors</Header4>

      <p>
        Customize the colors used in your client portal. All colors are required.
      </p>

      <SimpleBlock>
        <ResetColorsButton />
      </SimpleBlock>
      <Row6x6x>
        <ColorPickerWithText
          source="theme.fontColor1"
          label="Font Color 1 / Colored Background"
          defaultColor={defaultColors.fontColor1}
          hint="Main text color on colored backgrounds"
        />

        <ColorPickerWithText
          source="theme.fontColor2"
          label="Font Color 2 / White Background"
          defaultColor={defaultColors.fontColor2}
          hint="Text color on white/light backgrounds"
        />
      </Row6x6x>

      <Row6x6x>
        <ColorPickerWithText
          source="theme.detailBoxHighlightColor1"
          label="Detail Box Highlight 1"
          defaultColor={defaultColors.detailBoxHighlightColor1}
          hint="Primary highlight color for detail boxes"
        />

        <ColorPickerWithText
          source="theme.detailBoxHighlightColor2"
          label="Detail Box Highlight 2"
          defaultColor={defaultColors.detailBoxHighlightColor2}
          hint="Secondary highlight color for detail boxes"
        />
      </Row6x6x>

      <Row6x6x>
        <ColorPickerWithText
          source="theme.detailBoxShadingColor1"
          label="Detail Box Shading 1"
          defaultColor={defaultColors.detailBoxShadingColor1}
          hint="Background shading for detail boxes"
        />

        <ColorPickerWithText
          source="theme.detailBoxShadingColor2"
          label="Detail Box Shading 2"
          defaultColor={defaultColors.detailBoxShadingColor2}
          hint="Secondary background shading"
        />
      </Row6x6x>
    </FormTemplate>
  );
}
